import { useEffect, useMemo, useState } from "react";
import type {
  KundenartikelList,
  MaterialRef,
  Mengen,
  MengenRoom,
  MengenWall,
} from "@bidi-handel/core/schemas";
import { api, ApiError } from "../../../api/client";
import { Icon } from "../../../shell/Icon";

const PT_TO_MM_PAPER = 25.4 / 72;
const GEWERKE = ["rohbau", "trockenbau", "putz", "oberflaeche", "sonstiges"] as const;

/**
 * Display-Hinweis (#7 Set), welches Auto-Set eine Wand in der Bepreisung
 * bekommt — gewerk-basiert gespiegelt (die autoritative Code-Zuordnung +
 * editierbare Schnellkalkulation entstehen server-seitig und erscheinen im
 * Preise/Match-Tab). Bewusst KEIN Dicke-Katalog hier dupliziert.
 */
function autoSetHint(gewerk: MengenWall["gewerk"]): string | null {
  if (gewerk === "trockenbau") return "Auto-Set: Trockenbau (Rigips + UK + Spachtel + Schrauben)";
  if (gewerk === "putz") return "Auto-Set: Innenputz (beidseitig)";
  return null;
}

/**
 * Raum-Detail-Modal (P4 „LV-Text-Light" + P3/P1 Flächen-Override + P2c Wand-
 * Material). Bemustert Boden/Wand/Decke (Freitext ODER Artikel aus dem Stamm),
 * erlaubt eine manuelle Bodenfläche (z.B. vom Stempel abgelesene Terrasse) und
 * pflegt das Material der gezeichneten Wände. Speichert in einem PATCH; das
 * Backend recomputed Mengen + vor-LV (Stale-Prinzip).
 */
export function RaumDetailModal({
  tenantId,
  anfrageId,
  planIndex,
  ratio,
  room,
  onClose,
  onSaved,
}: {
  tenantId: string;
  anfrageId: string;
  planIndex: number;
  ratio: number;
  room: MengenRoom;
  onClose: () => void;
  onSaved: (mengen: Mengen) => void;
}) {
  const [label, setLabel] = useState<string>(room.label ?? "");
  const [boden, setBoden] = useState<MaterialRef>(room.bemusterung?.boden ?? {});
  const [wand, setWand] = useState<MaterialRef>(room.bemusterung?.wand ?? {});
  const [decke, setDecke] = useState<MaterialRef>(room.bemusterung?.decke ?? {});
  const [walls, setWalls] = useState<MengenWall[]>(room.walls ?? []);
  const [areaMode, setAreaMode] = useState<"behalten" | "manuell" | "polygon">(
    "behalten"
  );
  const [areaInput, setAreaInput] = useState<string>(String(room.bodenflaecheM2));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Kundenartikel einmal pro Modal laden (statt je MaterialRow) — treibt sowohl
  // den Bemusterungs-Picker als auch den Set-Hinweis am gewählten Artikel.
  const [kundenartikel, setKundenartikel] = useState<KundenartikelList | null>(null);
  const [kundenartikelFailed, setKundenartikelFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setKundenartikelFailed(false);
    api
      .getKundenartikel(tenantId)
      .then((d) => {
        if (!cancelled) setKundenartikel(d);
      })
      .catch(() => {
        if (!cancelled) setKundenartikelFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const trimmedLabel = label.trim();
      const patch: Parameters<typeof api.patchMengenRoom>[3] = {
        planIndex,
        bemusterung: {
          boden: refOrUndef(boden),
          wand: refOrUndef(wand),
          decke: refOrUndef(decke),
        },
        walls,
      };
      if (trimmedLabel && trimmedLabel !== room.label) {
        patch.label = trimmedLabel;
      }
      if (areaMode === "manuell") {
        const n = Number(areaInput.replace(",", "."));
        if (!Number.isFinite(n) || n <= 0) {
          setError("Manuelle Bodenfläche: bitte eine positive Zahl in m².");
          setSaving(false);
          return;
        }
        patch.bodenflaecheM2 = n;
      } else if (areaMode === "polygon") {
        patch.bodenQuelleReset = true;
      }
      const m = await api.patchMengenRoom(tenantId, anfrageId, room.roomId, patch);
      onSaved(m);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { hint?: string; error?: string } | null;
        setError(body?.hint ?? body?.error ?? err.message);
      } else setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: "min(96vw, 1040px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <div className="uppercase-mini">/ MENGEN / RAUM-DETAIL</div>
            <div className="modal-title">
              {room.label}{" "}
              <span className="dim mono" style={{ fontSize: 13 }}>
                {room.din277.sub}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Schliessen"
          >
            ×
          </button>
        </div>

        <div className="modal-body" style={{ display: "grid", gap: 18 }}>
          {/* Raum-Bezeichnung (#4) */}
          <section style={{ display: "grid", gap: 6 }}>
            <div className="uppercase-mini" style={{ color: "var(--acc)" }}>
              // BEZEICHNUNG
            </div>
            <input
              type="text"
              value={label}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setLabel(v);
              }}
              className="input"
              placeholder={room.label}
              aria-label="Raum-Bezeichnung"
            />
          </section>

          {/* Oberflächen-Bemusterung Boden/Wand/Decke — gilt pro Raum, nicht je Wand */}
          <section style={{ display: "grid", gap: 12 }}>
            <div className="uppercase-mini" style={{ color: "var(--acc)" }}>
              // OBERFLÄCHEN-BEMUSTERUNG (GANZER RAUM)
            </div>
            <div className="dim" style={{ fontSize: 12, marginTop: -4 }}>
              Gilt für die <strong>gesamte Fläche</strong> je Bauteil — Boden
              komplett, Decke komplett, Wände als <strong>eine
              Gesamt-Oberfläche</strong> (nicht pro Wand teilbar). Fließt in die
              vor-LV-Texte fürs Matching.
            </div>
            <MaterialRow
              label="Boden"
              kundenartikel={kundenartikel}
              kundenartikelFailed={kundenartikelFailed}
              value={boden}
              onChange={setBoden}
            />
            <MaterialRow
              label="Wand"
              kundenartikel={kundenartikel}
              kundenartikelFailed={kundenartikelFailed}
              value={wand}
              onChange={setWand}
            />
            <MaterialRow
              label="Decke"
              kundenartikel={kundenartikel}
              kundenartikelFailed={kundenartikelFailed}
              value={decke}
              onChange={setDecke}
            />
          </section>

          {/* Bodenfläche-Quelle (P1/P3) */}
          <section style={{ display: "grid", gap: 8 }}>
            <div className="uppercase-mini" style={{ color: "var(--acc)" }}>
              // BODENFLÄCHE
            </div>
            <div className="mono dim" style={{ fontSize: 12 }}>
              aktuell {room.bodenflaecheM2.toFixed(2)} m² · Quelle {room.quelleBoden}
              {room.ngfStempelM2 !== undefined && ` · NGF ${room.ngfStempelM2.toFixed(2)} m²`}
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
              <label style={{ display: "flex", gap: 5, alignItems: "center" }}>
                <input
                  type="radio"
                  checked={areaMode === "behalten"}
                  onChange={() => setAreaMode("behalten")}
                />
                <span>unverändert</span>
              </label>
              <label style={{ display: "flex", gap: 5, alignItems: "center" }}>
                <input
                  type="radio"
                  checked={areaMode === "manuell"}
                  onChange={() => setAreaMode("manuell")}
                />
                <span>manuell setzen</span>
              </label>
              {areaMode === "manuell" && (
                <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={areaInput}
                    onChange={(e) => setAreaInput(e.currentTarget.value)}
                    className="input mono"
                    style={{ width: 90 }}
                    aria-label="Manuelle Bodenfläche m²"
                  />
                  <span className="mono dim">m²</span>
                </span>
              )}
              <label style={{ display: "flex", gap: 5, alignItems: "center" }}>
                <input
                  type="radio"
                  checked={areaMode === "polygon"}
                  onChange={() => setAreaMode("polygon")}
                />
                <span>auf Polygonfläche zurück</span>
              </label>
            </div>
          </section>

          {/* Konstruktive Wände (P2c) — je gezeichneter Wand eine Konstruktions-Position */}
          <section style={{ display: "grid", gap: 8 }}>
            <div className="uppercase-mini" style={{ color: "var(--acc)" }}>
              // KONSTRUKTIVE WÄNDE — EINZELN GEZEICHNET ({walls.length})
            </div>
            <div className="dim" style={{ fontSize: 12, marginTop: -4 }}>
              Je gezeichnete Wand eine eigene <strong>Konstruktions-Position</strong>
              {" "}(Aufbau/Dicke). Im Plan über „Wände zeichnen" anlegen, hier Gewerk/
              Dicke/Material pflegen. <strong>Nicht</strong> mit der Oberflächen-
              Bemusterung oben kombinieren (Doppelzählung) — eine Variante pro Wand.
            </div>
            {walls.length === 0 && (
              <div className="dim mono" style={{ fontSize: 12 }}>
                Keine gezeichneten Wände — Wandfläche kommt aus Umfang × Raumhöhe.
              </div>
            )}
            {walls.map((w, i) => {
              const lengthM =
                (Math.hypot(w.end.x - w.start.x, w.end.y - w.start.y) *
                  PT_TO_MM_PAPER *
                  ratio) /
                1000;
              // #9: direkt zugewiesener Konstruktions-/Set-Artikel dieser Wand.
              const zugewiesenesSet =
                w.artikelCode && kundenartikel
                  ? kundenartikel.find((a) => a.code === w.artikelCode)?.stueckliste
                  : undefined;
              return (
                <div
                  key={w.id}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    flexWrap: "wrap",
                    borderTop: i > 0 ? "1px solid var(--line-soft, #2a2a2a)" : "none",
                    paddingTop: i > 0 ? 8 : 0,
                  }}
                >
                  <span className="mono dim" style={{ minWidth: 64 }}>
                    {lengthM.toFixed(2)} m
                  </span>
                  <select
                    className="input"
                    value={w.gewerk ?? ""}
                    onChange={(e) => {
                      const v = e.currentTarget.value as MengenWall["gewerk"];
                      setWalls((ws) =>
                        ws.map((x) =>
                          x.id === w.id ? { ...x, gewerk: v || undefined } : x
                        )
                      );
                    }}
                  >
                    <option value="">Gewerk …</option>
                    {GEWERKE.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Dicke mm"
                    value={w.dickeMm !== undefined ? String(w.dickeMm) : ""}
                    onChange={(e) => {
                      const n = Number(e.currentTarget.value.replace(",", "."));
                      setWalls((ws) =>
                        ws.map((x) =>
                          x.id === w.id
                            ? { ...x, dickeMm: Number.isFinite(n) && n > 0 ? n : undefined }
                            : x
                        )
                      );
                    }}
                    className="input mono"
                    style={{ width: 86 }}
                    aria-label="Wanddicke mm"
                  />
                  <input
                    type="text"
                    placeholder="Material (frei)"
                    value={w.materialName ?? ""}
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      setWalls((ws) =>
                        ws.map((x) =>
                          x.id === w.id ? { ...x, materialName: v || undefined } : x
                        )
                      );
                    }}
                    className="input"
                    style={{ flex: 1, minWidth: 120 }}
                  />
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => setWalls((ws) => ws.filter((x) => x.id !== w.id))}
                    title="Wand entfernen"
                  >
                    ×
                  </button>
                  {/* #9: Konstruktions-/Set-Artikel direkt dieser Wand zuweisen */}
                  <div
                    style={{
                      flexBasis: "100%",
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      paddingLeft: 64,
                    }}
                  >
                    <span
                      className="uppercase-mini"
                      style={{ color: "var(--fg-dim)", fontSize: 10, whiteSpace: "nowrap" }}
                    >
                      Konstr.-Artikel
                    </span>
                    <ArtikelQuickPick
                      list={kundenartikel}
                      loadFailed={kundenartikelFailed}
                      onPick={(code, bez) =>
                        setWalls((ws) =>
                          ws.map((x) =>
                            x.id === w.id
                              ? { ...x, artikelCode: code, artikelBezeichnung: bez }
                              : x
                          )
                        )
                      }
                    />
                    {w.artikelCode && (
                      <span className="pill acc mono" style={{ fontSize: 10 }}>
                        {w.artikelCode}
                        <button
                          type="button"
                          onClick={() =>
                            setWalls((ws) =>
                              ws.map((x) =>
                                x.id === w.id
                                  ? { ...x, artikelCode: undefined, artikelBezeichnung: undefined }
                                  : x
                              )
                            )
                          }
                          style={{
                            marginLeft: 6,
                            background: "none",
                            border: "none",
                            color: "inherit",
                            cursor: "pointer",
                          }}
                          aria-label="Konstruktions-Artikel entfernen"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {zugewiesenesSet && (
                      <span
                        className="pill mono"
                        style={{ fontSize: 10, whiteSpace: "nowrap" }}
                        title={`Set-Artikel — bildet die Schnellkalkulation dieser Konstruktions-Position aus ${zugewiesenesSet.komponenten.length} Komponenten (${
                          zugewiesenesSet.preisModus === "komplettpreis" ? "Komplettpreis" : "summiert"
                        }).`}
                      >
                        📦 Set · {zugewiesenesSet.komponenten.length} Komp.
                      </span>
                    )}
                  </div>
                  {/* Fallback: generisches Wand-Template nur wenn KEIN Artikel zugewiesen */}
                  {!w.artikelCode && autoSetHint(w.gewerk) && (
                    <span
                      className="mono"
                      style={{
                        flexBasis: "100%",
                        fontSize: 11,
                        color: "var(--acc)",
                        opacity: 0.85,
                        paddingLeft: 64,
                      }}
                      title="Kein Artikel zugewiesen → diese Wand wird in der Bepreisung mit dem generischen Set-Template belegt (im Preise-Tab prüfbar). Weise oben einen Set-Artikel zu, um das zu überschreiben."
                    >
                      → Fallback: {autoSetHint(w.gewerk)}
                    </span>
                  )}
                </div>
              );
            })}
          </section>

          {error && (
            <div
              className="warn-line"
              style={{ borderLeftColor: "var(--bad)", background: "oklch(0.72 0.18 25 / 0.08)" }}
            >
              <strong>Speichern fehlgeschlagen:</strong> <span className="mono">{error}</span>
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>
            Abbrechen
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn primary" onClick={save} disabled={saving}>
            {saving ? "Speichere …" : <><Icon name="check" size={14} /> Speichern</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function refOrUndef(r: MaterialRef): MaterialRef | undefined {
  const has = r.freitext?.trim() || r.artikelCode || r.artikelBezeichnung;
  return has ? r : undefined;
}

/** Eine Bemusterungs-Zeile: Freitext + Schnell-Auswahl eines Stamm-Artikels. */
function MaterialRow({
  label,
  kundenartikel,
  kundenartikelFailed,
  value,
  onChange,
}: {
  label: string;
  kundenartikel: KundenartikelList | null;
  kundenartikelFailed: boolean;
  value: MaterialRef;
  onChange: (v: MaterialRef) => void;
}) {
  // Ist der gewählte Artikel ein Set (mit Stückliste)? Treibt den Hinweis, dass
  // die Zuweisung im Angebot zu einer Schnellkalkulation expandiert wird.
  const gewaehltesSet =
    value.artikelCode && kundenartikel
      ? kundenartikel.find((a) => a.code === value.artikelCode)?.stueckliste
      : undefined;
  return (
    <div style={{ display: "grid", gap: 5 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span className="uppercase-mini" style={{ width: 52, color: "var(--fg-dim)" }}>
          {label}
        </span>
        <input
          type="text"
          placeholder="Material beschreiben (z.B. Fliesen 30x30)"
          value={value.freitext ?? ""}
          onChange={(e) => onChange({ ...value, freitext: e.currentTarget.value })}
          className="input"
          style={{ flex: 1 }}
        />
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", paddingLeft: 60 }}>
        <ArtikelQuickPick
          list={kundenartikel}
          loadFailed={kundenartikelFailed}
          onPick={(code, bez) =>
            onChange({ ...value, artikelCode: code, artikelBezeichnung: bez })
          }
        />
        {value.artikelCode && (
          <span className="pill acc mono" style={{ fontSize: 10 }}>
            {value.artikelCode}
            <button
              type="button"
              onClick={() =>
                onChange({
                  ...value,
                  artikelCode: undefined,
                  artikelBezeichnung: undefined,
                })
              }
              style={{
                marginLeft: 6,
                background: "none",
                border: "none",
                color: "inherit",
                cursor: "pointer",
              }}
              aria-label="Artikel-Auswahl entfernen"
            >
              ×
            </button>
          </span>
        )}
        {gewaehltesSet && (
          <span
            className="pill mono"
            style={{ fontSize: 10 }}
            title={`Set-Artikel — wird im Angebot zu einer Schnellkalkulation aus ${gewaehltesSet.komponenten.length} Komponenten (${
              gewaehltesSet.preisModus === "komplettpreis" ? "Komplettpreis" : "summiert"
            }) expandiert.`}
          >
            📦 Set · {gewaehltesSet.komponenten.length} Komp.
          </span>
        )}
      </div>
    </div>
  );
}

/** Inline-Artikelsuche über die Tenant-Kundenartikel (Substring über mehrere
 *  Felder). Liste wird vom Modal geladen + durchgereicht. */
function ArtikelQuickPick({
  list,
  loadFailed,
  onPick,
}: {
  list: KundenartikelList | null;
  loadFailed: boolean;
  onPick: (code: string, bezeichnung: string) => void;
}) {
  const [q, setQ] = useState("");

  const matches = useMemo(() => {
    if (!list || q.trim().length < 2) return [];
    const s = q.toLowerCase();
    return list
      .filter(
        (a) =>
          a.code.toLowerCase().includes(s) ||
          a.bezeichnung.toLowerCase().includes(s) ||
          a.warengruppe?.toLowerCase().includes(s) ||
          a.suchbegriffe.some((x) => x.toLowerCase().includes(s))
      )
      .slice(0, 12);
  }, [list, q]);

  const showEmpty = q.trim().length >= 2 && matches.length === 0;

  return (
    <div style={{ position: "relative", flex: 1 }}>
      <input
        type="text"
        placeholder="… oder Artikel aus Stammdaten wählen (≥ 2 Zeichen)"
        value={q}
        onChange={(e) => setQ(e.currentTarget.value)}
        className="input"
        style={{ width: "100%" }}
      />
      {matches.length > 0 && (
        <div className="artikel-pick-menu">
          {matches.map((a) => (
            <button
              key={a.code}
              type="button"
              className="artikel-pick-item"
              onClick={() => {
                onPick(a.code, a.bezeichnung);
                setQ("");
              }}
            >
              <span className="mono pick-code">{a.code}</span>
              <span>{a.bezeichnung}</span>
              {a.stueckliste && (
                <span
                  className="mono"
                  style={{ marginLeft: "auto", fontSize: 10, opacity: 0.8 }}
                  title={`Set-Artikel mit ${a.stueckliste.komponenten.length} Komponenten`}
                >
                  📦 Set
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {showEmpty && (
        <div className="artikel-pick-empty">
          {loadFailed
            ? "Stammdaten nicht ladbar — Backend/Tenant prüfen."
            : list && list.length === 0
              ? "Stammdaten leer — erst unter Stammdaten → Catalog Artikel ins Sortiment legen."
              : `Keine Treffer für „${q.trim()}".`}
        </div>
      )}
    </div>
  );
}
